/**
 * Authentication debugging utilities
 */
import { authStateManager } from "./authStateManager";
import { logger } from "../../utils/logger";
import Cookies from "universal-cookie";

export class AuthDebugger {
  private static instance: AuthDebugger;
  private cookies: Cookies;

  private constructor() {
    this.cookies = new Cookies();
  }

  public static getInstance(): AuthDebugger {
    if (!AuthDebugger.instance) {
      AuthDebugger.instance = new AuthDebugger();
    }
    return AuthDebugger.instance;
  }

  /**
   * Get comprehensive authentication debug information
   */
  public async getDebugInfo(): Promise<{
    timestamp: string;
    authState: any;
    validation: any;
    storageInfo: any;
    cookies: any;
    localStorage: any;
    issues: string[];
  }> {
    const issues: string[] = [];
    const timestamp = new Date().toISOString();

    // Get auth state
    const authState = authStateManager.getAuthState();

    // Get validation results
    const validation = authStateManager.validateAuthState();
    if (!validation.isValid) {
      issues.push(...validation.issues);
    }

    // Get storage information
    const storageInfo = await this.getStorageInfo();
    if (storageInfo.localStorageUsage.percentage > 90) {
      issues.push(
        `localStorage usage is high: ${storageInfo.localStorageUsage.percentage.toFixed(
          2
        )}%`
      );
    }

    // Get cookie information
    const cookies = {
      token: this.cookies.get("token") ? "Present" : "Missing",
      token_secret: this.cookies.get("token_secret") ? "Present" : "Missing",
      allCookies: this.cookies.getAll(),
    };

    // Get localStorage auth-related items
    const localStorageData = {
      loggedIn: localStorage.getItem("loggedIn"),
      token: localStorage.getItem("token") ? "Present" : "Missing",
      token_secret: localStorage.getItem("token_secret")
        ? "Present"
        : "Missing",
      tokenRefreshing: localStorage.getItem("tokenRefreshing"),
      redirectURL: localStorage.getItem("redirectURL"),
    };

    return {
      timestamp,
      authState,
      validation,
      storageInfo,
      cookies,
      localStorage: localStorageData,
      issues,
    };
  }

  /**
   * Get storage usage information
   */
  private async getStorageInfo(): Promise<any> {
    try {
      const { getLocalStorageUsage } = await import("./localStorageUtils");
      return {
        localStorageUsage: getLocalStorageUsage(),
        sessionStorageKeys: Object.keys(sessionStorage).length,
        localStorageKeys: Object.keys(localStorage).length,
      };
    } catch (error) {
      return {
        localStorageUsage: { used: 0, available: 0, percentage: 0 },
        sessionStorageKeys: 0,
        localStorageKeys: 0,
        error: "Could not get storage info",
      };
    }
  }

  /**
   * Log debug information
   */
  public async logDebugInfo(): Promise<void> {
    const debugInfo = await this.getDebugInfo();

    // Only show detailed debug info in development
    if (process.env.NODE_ENV === "development") {
      logger.info("=== AUTH DEBUG INFO ===");
      logger.info("Timestamp:", debugInfo.timestamp);
      logger.info("Auth State:", debugInfo.authState);
      logger.info("Validation:", debugInfo.validation);
      logger.info("Storage Info:", debugInfo.storageInfo);
      logger.info("Cookies:", debugInfo.cookies);
      logger.info("LocalStorage:", debugInfo.localStorage);

      if (debugInfo.issues.length > 0) {
        logger.warn("Issues found:", debugInfo.issues);
      } else {
        logger.info("No issues found");
      }
      logger.info("=== END AUTH DEBUG INFO ===");
    } else {
      // In production, only log issues
      if (debugInfo.issues.length > 0) {
        logger.warn("Auth issues found:", debugInfo.issues);
      }
    }
  }

  /**
   * Export debug information as JSON
   */
  public async exportDebugInfo(): Promise<string> {
    const debugInfo = await this.getDebugInfo();
    return JSON.stringify(debugInfo, null, 2);
  }

  /**
   * Check for common authentication issues
   */
  public async checkCommonIssues(): Promise<string[]> {
    const issues: string[] = [];
    const debugInfo = await this.getDebugInfo();

    // Check for token/cookie mismatches
    if (
      debugInfo.authState.hasValidToken &&
      debugInfo.cookies.token === "Missing"
    ) {
      issues.push("Token exists in state but not in cookies");
    }

    if (
      debugInfo.authState.hasValidRefreshToken &&
      debugInfo.cookies.token_secret === "Missing"
    ) {
      issues.push("Refresh token exists in state but not in cookies");
    }

    // Check for localStorage/cookie mismatches
    if (
      debugInfo.localStorage.token === "Present" &&
      debugInfo.cookies.token === "Missing"
    ) {
      issues.push("Token in localStorage but not in cookies");
    }

    if (
      debugInfo.localStorage.token_secret === "Present" &&
      debugInfo.cookies.token_secret === "Missing"
    ) {
      issues.push("Refresh token in localStorage but not in cookies");
    }

    // Check for authentication state inconsistencies
    if (
      (document.cookie.includes("token=") ||
        document.cookie.includes("token_secret=")) &&
      !debugInfo.authState.isAuthenticated
    ) {
      issues.push(
        "localStorage says logged in but auth state says not authenticated"
      );
    }

    // Check for stale refresh flags
    if (debugInfo.localStorage.tokenRefreshing === "true") {
      issues.push(
        "Token refresh flag is still set - may indicate stuck refresh process"
      );
    }

    return issues;
  }

  /**
   * Auto-fix common issues
   */
  public async autoFixIssues(): Promise<boolean> {
    const issues = await this.checkCommonIssues();

    if (issues.length === 0) {
      logger.info("No issues to fix");
      return true;
    }

    logger.warn("Attempting to auto-fix issues:", issues);

    try {
      // Try to fix authentication state inconsistencies
      const fixed = authStateManager.fixAuthStateInconsistencies();

      if (fixed) {
        logger.info("Successfully fixed authentication state inconsistencies");
      } else {
        logger.warn(
          "Could not fix authentication state inconsistencies, clearing auth state"
        );
        authStateManager.clearAuthState();
      }

      // Clear stale refresh flags
      localStorage.removeItem("tokenRefreshing");

      return true;
    } catch (error) {
      logger.error("Error during auto-fix:", error);
      return false;
    }
  }
}

// Export singleton instance
export const authDebugger = AuthDebugger.getInstance();

// Add to window for debugging in console (development only)
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  (window as any).authDebugger = authDebugger;
  (window as any).authStateManager = authStateManager;
  logger.log("Auth debugging tools available: authDebugger, authStateManager");
}
